import type { ConnectionRef } from "@shared/components/fwaas/FwaasData";
import type { ConnectionType } from "@shared/components/fwaas/ConnectionsHelpers";
import { serviceDetailPath, type ServiceConnectionType } from "@shared/navigation/tabs/detailRoutes";

/** One connection of a Virtual Firewall, with the kind of service it is. */
export interface ConnectionRow {
    type: ConnectionType;
    item: ConnectionRef;
}

/** The service types an interface can ride on, as the backend names them. */
const CONNECTED_SERVICE_TYPES: Record<string, ServiceConnectionType> = {
    port: "port",
    internet: "internet",
};

/** How the service an interface rides on is named: "Port 10-B5F9F9". */
const CONNECTED_SERVICE_LABEL: Record<string, string> = {
    port: "Port",
    internet: "Internet",
};

/** "Port 10-B5F9F9": the service an interface rides on; null when it rides on nothing. */
export function connectedServiceLabel(item: ConnectionRef): string | null {
    if (!item.connectedServiceSubId) return null;
    const kind = CONNECTED_SERVICE_LABEL[item.connectedServiceType?.toLowerCase() ?? ""];
    return kind ? `${kind} ${item.connectedServiceSubId}` : item.connectedServiceSubId;
}

/**
 * Where a FWINT / SDWAN-INT leads: the port or the Internet connection it rides on, by
 * the type the backend reports. Null when that service is unknown — the row then shows
 * it as text instead of a link that would open the wrong detail.
 */
function connectedServicePath(item: ConnectionRef): string | null {
    const type = CONNECTED_SERVICE_TYPES[item.connectedServiceType?.toLowerCase() ?? ""];
    return type && item.connectedServiceId != null
        ? serviceDetailPath(type, item.connectedServiceId)
        : null;
}

/**
 * Where a row's Connection leads. An interface has no detail of its own, so it opens the
 * service it rides on, which the cell names in parentheses (CTX-931). Before, it always
 * opened a port, even for an interface on an Internet connection.
 */
export function connectionPath({ type, item }: ConnectionRow): string | null {
    if (type === "fwint" || type === "sdwanInt") return connectedServicePath(item);
    return serviceDetailPath(type, item.id || item.subscriptionId);
}
